import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type DrawerDirection = 'top' | 'right' | 'bottom' | 'left';

export interface DrawerProps {
  open: boolean;
  direction?: DrawerDirection;
  ariaLabel: string;
  children: ReactNode;
  panelClassName?: string;
  backdropClassName?: string;
  closeOnBackdrop?: boolean;
  onClose: () => void;
}

const directionClasses: Record<DrawerDirection, string> = {
  top: 'inset-x-0 top-0 max-h-[min(80dvh,640px)] w-full rounded-b-2xl border-b',
  right: 'inset-y-0 right-0 h-full max-w-[min(90vw,420px)] w-full rounded-l-2xl border-l',
  bottom: 'inset-x-0 bottom-0 max-h-[min(80dvh,640px)] w-full rounded-t-2xl border-t',
  left: 'inset-y-0 left-0 h-full max-w-[min(90vw,420px)] w-full rounded-r-2xl border-r',
};

export const Drawer = ({
  open,
  direction = 'right',
  ariaLabel,
  children,
  panelClassName = '',
  backdropClassName = '',
  closeOnBackdrop = true,
  onClose,
}: DrawerProps) => {
  const panelRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  const contentRef = useRef({ ariaLabel, children, direction, panelClassName, backdropClassName });
  const hasOpenedRef = useRef(open);
  onCloseRef.current = onClose;
  if (open) {
    hasOpenedRef.current = true;
    contentRef.current = { ariaLabel, children, direction, panelClassName, backdropClassName };
  }
  const content = contentRef.current;

  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    document.body.style.overflow = 'hidden';
    const frame = requestAnimationFrame(() => panelRef.current?.focus());
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [open]);

  return createPortal(
    <div
      className={`drawer-backdrop fixed inset-0 z-100 bg-black/60 ${content.backdropClassName}`.trim()}
      data-state={open ? 'open' : 'closed'}
      inert={!open}
      onMouseDown={(event) => {
        if (open && closeOnBackdrop && event.target === event.currentTarget) {
          onCloseRef.current();
        }
      }}
    >
      {hasOpenedRef.current ? (
        <section
          ref={panelRef}
          className={`drawer-panel fixed flex overflow-hidden border-(--line) bg-(--surface) shadow-2xl ${
            directionClasses[content.direction]
          } ${content.panelClassName}`.trim()}
          data-direction={content.direction}
          role="dialog"
          aria-modal="true"
          aria-label={content.ariaLabel}
          tabIndex={-1}
        >
          {content.children}
        </section>
      ) : null}
    </div>,
    document.body,
  );
};
